<template>
  <RtCard
    :mode="mode"
    :icon="RT_CONFIG_SECTION_META.travel.icon"
    :title="t('rt.tabs.triggerTravel')"
    :loading="loading"
    @update:mode="(v) => emit('update:mode', v)"
  >
    <div class="w-full h-full flex flex-col justify-center items-center">
      <div class="w-full flex items-start gap-2.5">
        <div class="flex-1 relative">
          <Slider
            :model-value="currentValue === undefined ? [] : [currentValue]"
            :min="minLevel"
            :max="maxLevel"
            :step="stepLevel"
            :disabled="loading || currentValue === undefined"
            thumb-alignment="contain"
            range-alignment="thumb-end"
            :thumb-size="THUMB_WIDTH"
            animated
            class="w-full relative h-8"
            track-class="bg-glass30 h-8"
            range-class="bg-blue-light-hover "
            thumb-class="bg-transparent rounded-none"
            @update:model-value="onDrag"
            @value-commit="onCommit"
          >
            <template #thumb>
              <div class="relative flex items-center justify-center w-full h-full">
                <Icon icon="Direction-FIll-SwipeLR1" class="size-8" />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] -top-1 -translate-x-1/2"
                  :class="'h-4'"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] -bottom-1 -translate-x-1/2"
                  :class="'h-4'"
                />
              </div>
            </template>
          </Slider>
          <!-- 刻度：两端用「短 / 长」表示行程方向，中间档位保留竖线。 -->
          <div class="relative mt-2 h-4 overflow-hidden rounded-full">
            <div
              v-if="isRealTimeActive"
              class="absolute inset-y-0 left-0 bg-glass30 pointer-events-none transition-[width] duration-75"
              :style="{
                width: `calc(${THUMB_HALF}px + ${realTimePercent / 100} * (100% - ${THUMB_WIDTH}px))`,
              }"
              aria-hidden="true"
            />
            <button
              v-for="tick in tickMarks"
              :key="tick.value"
              type="button"
              :disabled="loading || currentValue === undefined"
              class="absolute inset-y-0 z-10 w-4 -translate-x-1/2 flex items-center justify-center cursor-pointer select-none"
              :style="{
                left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
              }"
              :aria-label="t('rt.travel.setValue', { value: tick.value })"
              @click="jumpToTick(tick.value)"
            >
              <span
                v-if="tick.value === minLevel || tick.value === maxLevel"
                class="text-s-medium whitespace-nowrap transition-colors"
                :class="
                  currentValue === tick.value ? 'text-brand-blue1' : 'text-text-glass-default'
                "
              >
                {{ t(tick.value === minLevel ? 'rt.common.short' : 'rt.common.long') }}
              </span>
              <span
                v-else
                class="block w-px h-3 transition-colors"
                :class="currentValue === tick.value ? 'bg-brand-blue1' : 'bg-text-glass-default'"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <RtTitleValueInput
          :model-value="displayValue"
          :min="minLevel"
          :max="maxLevel"
          :disabled="loading || currentValue === undefined"
          :tooltip="hasDivergedValues ? t('rt.travel.differentValues') : undefined"
          @commit="commitValue"
        />
        <RtRestoreButton
          :disabled="loading || currentValue === undefined || currentValue === defaultValue"
          @confirm="resetToDefault"
        />
      </div>
    </div>
  </RtCard>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Icon, Slider } from '@rapoo/element'
  import RtCard from '../RtCard.vue'
  import RtRestoreButton from '../RtRestoreButton.vue'
  import RtTitleValueInput from '../RtTitleValueInput.vue'
  import { RT_CONFIG_SECTION_META, type RtSelectionMode } from '../../types'
  import type { MouseRTValueConfig, RtSide, TriggerStatus } from '@rapoo/device-sdk'

  type SideMap<T> = Partial<Record<RtSide, T>>

  const { t } = useI18n()

  const props = defineProps<{
    triggerPress?: SideMap<number>
    /** 当前产品 manifest 声明的行程范围与默认值。 */
    config: MouseRTValueConfig
    /**
     * 设备最近一次上报的实时行程状态 (0xB5)。
     * 仅在「按键触发行程」tab 启用轮询时由父组件传入；切走后该值会被父组件清空。
     */
    triggerStatus?: TriggerStatus
    loading?: boolean
    mode: RtSelectionMode
  }>()

  const emit = defineEmits<{
    commit: [
      payload: {
        field: 'triggerPress'
        mode: RtSelectionMode
        value: number
      },
    ]
    'update:mode': [value: RtSelectionMode]
  }>()

  const minLevel = computed(() => props.config.min)
  const maxLevel = computed(() => props.config.max)
  const stepLevel = 1
  /** thumb 尺寸：与 Slider 的 thumb-class h-10 w-8 对应 */
  const THUMB_WIDTH = 32
  const THUMB_HALF = THUMB_WIDTH / 2

  const sliderValue = ref<number[]>([])

  const modeValue = computed<number | undefined>(() => {
    if (props.mode === 'left') return props.triggerPress?.left
    if (props.mode === 'right') return props.triggerPress?.right
    return props.triggerPress?.left
  })

  const defaultValue = computed(() => props.config.default)

  /**
   * 当前模式下设备实时上报的行程级别（0-10；来自父组件轮询 0xB5 的结果）。
   * 仅在「按键触发行程」tab 启用轮询；切到其他 tab 时父组件会清空此值，回退为 undefined。
   */
  const realTimeLevel = computed<number | undefined>(() => {
    const status = props.triggerStatus
    if (!status) return undefined
    if (props.mode === 'left') return status.leftTriggerLevel
    if (props.mode === 'right') return status.rightTriggerLevel
    // both 模式与 modeValue 保持一致，取左键级别
    return status.leftTriggerLevel
  })

  /**
   * 当前模式下按键是否处于「按下」状态：实时级别 > 0 即视为正在触发行程，
   * 用于把数值染成蓝色高亮，与「当前灵敏度」被开关关闭时灰显的处理保持一致。
   */
  const isRealTimeActive = computed(() => {
    const level = realTimeLevel.value
    return level !== undefined && level > 0
  })

  /**
   * 实时位置在刻度有效区间内的百分比。范围条终点与对应刻度中心对齐；
   * 0 表示当前没有按键行程，不绘制范围条。
   */
  const realTimePercent = computed(() => {
    const level = realTimeLevel.value
    if (level === undefined || level <= 0) return 0
    const range = maxLevel.value - minLevel.value
    if (range <= 0) return 0
    const clamped = Math.min(maxLevel.value, Math.max(minLevel.value, level))
    return ((clamped - minLevel.value) / range) * 100
  })

  const currentValue = computed(() => sliderValue.value[0] ?? modeValue.value)

  /** 刻度标记及其百分比位置 */
  const tickMarks = computed(() => {
    const result: { value: number; percent: number }[] = []
    const max = maxLevel.value
    const range = max - minLevel.value
    for (let i = minLevel.value; i <= max; i++) {
      result.push({
        value: i,
        percent: range > 0 ? ((i - minLevel.value) / range) * 100 : 0,
      })
    }
    return result
  })

  /** 点击刻度直接跳转 */
  function jumpToTick(value: number) {
    const snapped = snap(value)
    sliderValue.value = [snapped]
    emit('commit', { field: 'triggerPress', mode: props.mode, value: snapped })
  }

  watch(
    [() => props.mode, () => props.triggerPress?.left, () => props.triggerPress?.right],
    () => {
      sliderValue.value = modeValue.value === undefined ? [] : [modeValue.value]
    },
    { immediate: true },
  )

  function snap(value: number): number {
    const next = Math.round(value / stepLevel) * stepLevel
    return Math.min(maxLevel.value, Math.max(minLevel.value, next))
  }

  function onDrag(value?: number[]) {
    if (value?.[0] === undefined) return
    sliderValue.value = [snap(value[0])]
  }

  function onCommit(value: number[]) {
    if (value[0] === undefined) return
    const next = snap(value[0])
    sliderValue.value = [next]
    emit('commit', { field: 'triggerPress', mode: props.mode, value: next })
  }

  /** 还原按钮：将当前模式下的行程点恢复到 manifest 配置的初始值 */
  function resetToDefault() {
    const target = snap(defaultValue.value)
    sliderValue.value = [target]
    emit('commit', { field: 'triggerPress', mode: props.mode, value: target })
  }

  /**
   * 手动输入框：草稿值与展示值的来源分离，避免边输入边触发外层 emit。
   * - `displayValue` 决定输入框「平时显示什么」：单侧取该侧；'both' 时若两侧不同显示 `/`。
   * - `draftValue` 仅在编辑过程中作为输入框的实际 model，由 `handleDraft` / `commitValue` 维护。
   * - `hasDivergedValues` 决定 Tooltip 是否启用（提示「左右键行程点设置不同」）。
   *
   * 注意：与 RtSensitivityCard 不同，本卡 slider 在开关关闭时仍可调（只是约束解除），
   * 所以输入框只跟随 `loading` 禁用，不跟随 `isSwitchOn`。
   */
  const leftTravel = computed(() => props.triggerPress?.left)
  const rightTravel = computed(() => props.triggerPress?.right)

  const hasDivergedValues = computed(
    () =>
      props.mode === 'both' &&
      leftTravel.value !== undefined &&
      rightTravel.value !== undefined &&
      leftTravel.value !== rightTravel.value,
  )

  const displayValue = computed(() => {
    if (props.mode === 'left') return leftTravel.value === undefined ? '' : String(leftTravel.value)
    if (props.mode === 'right')
      return rightTravel.value === undefined ? '' : String(rightTravel.value)
    if (hasDivergedValues.value) return '/'
    if (leftTravel.value !== undefined) return String(leftTravel.value)
    if (rightTravel.value !== undefined) return String(rightTravel.value)
    return ''
  })

  /** 手输值由共享输入框完成校验和夹取；both 模式会同步写入两侧。 */
  function commitValue(value: number) {
    emit('commit', { field: 'triggerPress', mode: props.mode, value })
  }
</script>
